/**
 * The definitions every environment is expected to have.
 *
 * Membership in THIS list is a claim: the app reads the key, the default below
 * is what every organization without an override gets, and an environment that
 * lacks it is reported as Missing on /admin/app-config. The three sibling
 * lists each make a different claim — see `./index.ts`.
 *
 * `appId` appears nowhere here. It is an environment fact, injected at call
 * time from `config.veritoneAppId`, not something a cross-environment manifest
 * can pin.
 *
 * A `defaultValue` is OMITTED, never `''`, where the definition genuinely
 * declares none. The two are different values and `diffDefinitions` compares
 * them as such; an entry defaulting to `''` would report drift against an
 * environment that declares nothing, on every scan, forever.
 *
 * Every default here was read off the code that consumes it, because changing
 * one is a behaviour change and not a documentation change.
 *
 * REGISTERING A KEY CAN ALSO CHANGE WHICH SOURCE DECIDES, and it depends on
 * which reader the key goes through in `user-permissions/selectors.ts`. There
 * are two, and they behave oppositely on a registered `"false"`:
 *
 *  - `booleanAppConfig` reads the RAW record and treats its presence as
 *    authoritative, including when it is false. Registering a flag defaulting
 *    to `"false"` here HARD-DISABLES a feature an environment had switched on
 *    through its `featureFlags` blob. In this list: the three redaction modes
 *    (`enableVideoRedaction`, `enableAudioRedaction`, `enableImageRedaction`),
 *    which is why all three default to `"true"`.
 *
 *  - `!!appConfigs.x || featureFlags.x` reads the map from
 *    `selectApplicationConfigs`, which DROPS falsy values — so a registered
 *    `"false"` leaves the key absent from that map and the feature-flag half
 *    still decides. Registering it takes nothing away. In this list:
 *    `folderView`, `disableUpload`, `voiceMask`, `inverseBlur`,
 *    `enableDocumentRedaction` and `enableSpeakerSeparation`.
 *
 * Check which one a key uses before choosing its default. This header
 * previously stated the first rule as though it were the only one, which is
 * true of three entries here and wrong about the other six.
 */

import { ManifestDefinition } from '@redact-modules/admin/models';
export const STANDARD_DEFINITIONS: readonly ManifestDefinition[] = [
  {
    configKey: 'folderView',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Use Redact Folder View',
    defaultValue: 'false',
  },
  {
    configKey: 'disableUpload',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Feature Flag: hide all upload buttons & disable upload endpoints / API ingest endpoints',
    defaultValue: 'false',
  },
  {
    configKey: 'voiceMask',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Voice Mask',
    defaultValue: 'true',
  },
  {
    /**
     * Opt-in, resolved exactly like `enableDocumentRedaction` below:
     * `!!appConfigs.x || featureFlags.x`. NOT the default-enabled shape the
     * redaction modes use — an org turns speaker separation on once the
     * engine-side path is ready for them.
     */
    configKey: 'enableSpeakerSeparation',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature flag: enable running the speaker separation engine',
    defaultValue: 'false',
  },
  {
    configKey: 'inverseBlur',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Inverse Blur',
    defaultValue: 'true',
  },
  {
    configKey: 'enableDocumentRedaction',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Enable Document Redaction',
    defaultValue: 'false',
  },
  {
    configKey: 'enableVideoRedaction',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Enable Video Redaction',
    defaultValue: 'true',
  },
  {
    configKey: 'enableImageRedaction',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Enable Image Redaction',
    defaultValue: 'true',
  },
  {
    configKey: 'enableAudioRedaction',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Enable Audio Redaction',
    defaultValue: 'true',
  },
  {
    /**
     * The one default where being wrong loses data: `0` or absent disables Trash
     * entirely, making every delete immediate and permanent. An environment where
     * this drifted to `"0"` would be destroying files with no visible error.
     */
    configKey: 'trashRetentionDays',
    configType: 'String',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Redact: Days an item stays in Trash before permanent deletion. 0 or absent disables Trash (delete is immediate/permanent).',
    defaultValue: '30',
  },
];
